import {
    AnchorHTMLAttributes,
    ReactNode,
    forwardRef,
    ForwardedRef,
} from "react";
import { cx } from "../../utils/cx";
import styles from "./Link.module.scss";

/**
 * Color / emphasis (Figma: Primary, Secondary, Inverted; plus `danger` for destructive).
 */
export type LinkVariant = "primary" | "secondary" | "inverted" | "danger";

export type LinkSize = "sm" | "md" | "lg";

/** `always` | `hover` (+ focus-visible) | `none` — Figma underline rows. */
export type LinkUnderline = "always" | "hover" | "none";

export type LinkProps = Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    "href"
> & {
    href: string;
    variant?: LinkVariant;
    size?: LinkSize;
    underline?: LinkUnderline;
    /** Sets `target="_blank"` and safe `rel` for external URLs. */
    external?: boolean;
    iconLeft?: ReactNode;
    iconRight?: ReactNode;
    /**
     * Renders a non-interactive span (anchors cannot be disabled in HTML).
     */
    disabled?: boolean;
};

function ExternalIcon() {
    return (
        <svg
            aria-hidden
            fill="none"
            height="1em"
            viewBox="0 0 16 16"
            width="1em"
        >
            <path
                d="M10 2h4v4M14 2 6 10M8 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.25"
            />
        </svg>
    );
}

/**
 * Treez DS — Link
 *
 * Inline link matrix (Figma `7335:50758`). Primary = brand green; Secondary =
 * body text black; Inverted = light text on dark surfaces. Underline modes
 * match Always / on hover / never. Focus ring follows `currentColor`.
 *
 * For React Router, reuse styles: `className` from a wrapping pattern or keep
 * `href` for full-page navigation.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
    {
        href,
        variant = "secondary",
        size = "md",
        underline = "hover",
        external,
        iconLeft,
        iconRight,
        disabled,
        className,
        children,
        rel,
        target,
        ...rest
    }: LinkProps,
    ref: ForwardedRef<HTMLAnchorElement>,
) {
    const mergedRel =
        external === true
            ? [rel, "noopener", "noreferrer"].filter(Boolean).join(" ") ||
              "noopener noreferrer"
            : rel;
    const mergedTarget = external ? "_blank" : target;

    if (disabled) {
        return (
            <span
                aria-disabled="true"
                className={cx(styles.root, className)}
                data-size={size}
                data-ui="link"
                data-underline={underline}
                data-variant={variant}
            >
                {iconLeft ? <span aria-hidden>{iconLeft}</span> : null}
                <span className={styles.label}>{children}</span>
                {iconRight ? <span aria-hidden>{iconRight}</span> : null}
                {external ? <ExternalIcon /> : null}
            </span>
        );
    }

    return (
        <a
            className={cx(styles.root, className)}
            data-size={size}
            data-ui="link"
            data-underline={underline}
            data-variant={variant}
            href={href}
            ref={ref}
            rel={mergedRel}
            target={mergedTarget}
            {...rest}
        >
            {iconLeft ? <span aria-hidden>{iconLeft}</span> : null}
            <span className={styles.label}>{children}</span>
            {iconRight ? <span aria-hidden>{iconRight}</span> : null}
            {external ? <ExternalIcon /> : null}
        </a>
    );
});

Link.displayName = "Link";
